import React from 'react';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Banner from 'components/UI/Banner';
import WarningBanner from 'components/UI/WarningBanner';
import Button from 'components/UI/Button';
import { Main, Background, Shade } from 'styles/themeColors';
import { ConsignmentDetails } from 'data/Consignment';
import { CONSIGNMENT_TYPES } from 'constants/order';
import { CONSIGNMENT_MODIFICATION_STATUS } from 'constants/consignmentEntry';
import { TEXT_LABELS } from 'constants/order';
import { CREDIT_SOURCE_ACTIVITY_STATUS, CREDIT_SOURCES } from 'constants/credit';
import { RentModifiedOnHold } from '../RentalOrderStates';
import { PermissionCode } from 'data/UserPermissions';
import { getConsignmentOnHoldReasonHeader, getConsignmentOnHoldReasonText } from 'utils/consignment';
import { navigate } from 'hooks/useHookRouterCompat';

interface CreditCheckV2BannerProps {
  consignmentDetailsResponse: ConsignmentDetails;
  refreshConsignment: () => void;
  onDeclineOrder: () => void;
  onSetupCompanyRelations: () => void;
  onRecheckCredit: () => void;
  onOverrideCredit: () => void;
  onRaiseCredit: () => void;
  companyId?: string | number;
  isReadOnly: boolean;
  isRecheckLoading: boolean;
  isOverrideLoading: boolean;
}

const CreditCheckV2Banner: React.FC<CreditCheckV2BannerProps> = ({
  consignmentDetailsResponse,
  refreshConsignment,
  onDeclineOrder,
  onSetupCompanyRelations,
  onRecheckCredit,
  onOverrideCredit,
  onRaiseCredit,
  companyId,
  isReadOnly,
  isRecheckLoading,
  isOverrideLoading
}) => {
  const companyRelation = consignmentDetailsResponse.businessUnitCompany;
  const creditId = companyRelation?.creditDetails?.creditId;
  const creditSource = companyRelation?.creditDetails?.source;
  const sourceActive = companyRelation?.creditDetails?.companyMdmDetails?.idStatus?.toLowerCase() === CREDIT_SOURCE_ACTIVITY_STATUS.ACTIVE;

  const handleCreditSettings = () => {
    navigate(`/companies/company-details/${consignmentDetailsResponse.order.company && consignmentDetailsResponse.order.company.id}`)
  }

  const buttons = (
    <FlexboxContainer
      flexDirection='row'
      justifyContent='flex-end'
      gap='4px'
    >
      <Button
        isGhost
        borderColor={Shade.shade550}
        color={Background.navy}
        onClick={onDeclineOrder}
        permissionCode={PermissionCode.APPROVE_CREDIT}
      >
        {TEXT_LABELS.DECLINE_ORDER}
      </Button>
      {
        !creditId ? (
          <Button
            borderColor={Background.navy}
            bgColor={Background.navy}
            onClick={onSetupCompanyRelations}
            disabled={!companyId}
            permissionCode={PermissionCode.APPROVE_CREDIT}
          >
            {TEXT_LABELS.SETUP_COMPANY_RELATIONS}
          </Button>
        ) : creditSource === CREDIT_SOURCES.MDM ? (
          <>
            <Button
              onClick={onOverrideCredit}
              bgColor={Background.navy}
              borderColor={Background.navy}
              permissionCode={PermissionCode.APPROVE_CREDIT}
              disabled={isOverrideLoading}
            >
              {TEXT_LABELS.OVERRIDE_CREDIT_DEFICIT_ACCEPT_ORDER}
            </Button>
            <Button
              borderColor={Background.navy}
              bgColor={Background.navy}
              onClick={onRecheckCredit}
              disabled={!companyId || isRecheckLoading}
            >
              {TEXT_LABELS.RECHECK_CREDIT}
            </Button>
            {
              !sourceActive &&
              <Button
                borderColor={Background.navy}
                bgColor={Background.navy}
                onClick={handleCreditSettings}
                disabled={!companyId}
              >
                {TEXT_LABELS.CREDIT_SETTINGS}
              </Button>
            }
          </>
        ) : (
          <>
            <Button
              onClick={onOverrideCredit}
              bgColor={Background.navy}
              borderColor={Background.navy}
              permissionCode={PermissionCode.APPROVE_CREDIT}
              disabled={isOverrideLoading}
            >
              {TEXT_LABELS.OVERRIDE_CREDIT_DEFICIT_ACCEPT_ORDER}
            </Button>
            {
              !isReadOnly && (
                <Button
                  onClick={onRaiseCredit}
                  borderColor={Background.navy}
                  bgColor={Background.navy}
                  permissionCode={PermissionCode.APPROVE_CREDIT}
                >
                  {TEXT_LABELS.RAISE_COMPANY_CREDIT}
                </Button>
              )
            }
          </>
        )
      }
    </FlexboxContainer >
  );

  return (
    <FlexboxContainer flexDirection='column'>
      {
        consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT &&
        <WarningBanner message={TEXT_LABELS.RENT_MEASUREMENT_UNIT_INFO} fontSize='16px' />
      }
      <Banner
        bgColor={Main.warning}
        title={getConsignmentOnHoldReasonHeader(consignmentDetailsResponse)}
        text={getConsignmentOnHoldReasonText(consignmentDetailsResponse)}
        buttons={buttons}
      />
      {
        consignmentDetailsResponse.modificationEntry?.status === CONSIGNMENT_MODIFICATION_STATUS.REQUESTED &&
        <RentModifiedOnHold consignmentData={consignmentDetailsResponse} refreshConsignment={refreshConsignment} />
      }
    </FlexboxContainer>
  );
};

export default CreditCheckV2Banner;